import { AnalyticIdentifier } from '@common/neo.tax';
import { ReactComponent as BeggingHandCoin } from '@ui-components/neo.tax/dist/icons/begging-hand-coin-2.svg';
import { ReactComponent as CompanyIcon } from '@ui-components/neo.tax/dist/icons/company.svg';
import { ReactComponent as EmployeeIcon } from '@ui-components/neo.tax/dist/icons/employee.svg';
import { ReactComponent as QreIcon } from '@ui-components/neo.tax/dist/icons/lab-protection-glasses.svg';
import { ReactComponent as DataSourcesIcon } from '@ui-components/neo.tax/dist/icons/link-accounting.svg';
import { ReactComponent as ProjectsIcon } from '@ui-components/neo.tax/dist/icons/presentation-projector-screen-hierarchy.svg';
import { ReactComponent as ExportIcon } from '@ui-components/neo.tax/dist/icons/receipt-1.svg';

import { getPath, NavSection, Path } from '@neo.tax/shared';
import { SideNavItemProps, SideNavProps } from '@neo.tax/shared/organisms';

export const rdPaths = [
  Path.RD_TAX_CREDIT,
  Path.COMPANY_INFO,
  Path.RD_PROJECTS,
  Path.PAYROLL_QRE,
  Path.ACCOUNTING_QRE,
  Path.CREDIT_CALCULATION,
  Path.RD_DATA_SOURCES,
  Path.EXPORT_TAX,
] as const;

export type RdPath = (typeof rdPaths)[number];

interface RdPathItem {
  section: NavSection.RD_TAX_CREDIT;
  path: Exclude<RdPath, Path.RD_TAX_CREDIT>;
  title: string;
  icon: React.ReactElement;
  id: string;
  hideIfNonFinancialUser: boolean;
}

export const RD_PATHS: RdPathItem[] = [
  {
    section: NavSection.RD_TAX_CREDIT,
    path: Path.COMPANY_INFO,
    title: 'Company Info',
    icon: <CompanyIcon />,
    id: `${AnalyticIdentifier.ENTERPRISE_NAV_COMPANY_INFO}`,
    hideIfNonFinancialUser: false,
  },
  {
    section: NavSection.RD_TAX_CREDIT,
    path: Path.RD_DATA_SOURCES,
    title: 'Data Sources',
    icon: <DataSourcesIcon />,
    id: `${AnalyticIdentifier.ENTERPRISE_NAV_RD_DATA_SOURCES}`,
    hideIfNonFinancialUser: false,
  },
  {
    section: NavSection.RD_TAX_CREDIT,
    path: Path.RD_PROJECTS,
    title: 'Projects',
    icon: <ProjectsIcon />,
    id: `${AnalyticIdentifier.ENTERPRISE_NAV_RD_PROJECTS}`,
    hideIfNonFinancialUser: false,
  },
  {
    section: NavSection.RD_TAX_CREDIT,
    path: Path.PAYROLL_QRE,
    title: 'Wage QREs',
    icon: <EmployeeIcon />,
    id: `${AnalyticIdentifier.ENTERPRISE_NAV_PAYROLL_QRE}`,
    hideIfNonFinancialUser: false,
  },
  {
    section: NavSection.RD_TAX_CREDIT,
    path: Path.ACCOUNTING_QRE,
    title: 'Other QREs',
    icon: <QreIcon />,
    id: `${AnalyticIdentifier.ENTERPRISE_NAV_ACCOUNTING_QRE}`,
    hideIfNonFinancialUser: true,
  },
  {
    section: NavSection.RD_TAX_CREDIT,
    path: Path.CREDIT_CALCULATION,
    title: 'Credit Calculation',
    icon: <BeggingHandCoin />,
    id: `${AnalyticIdentifier.ENTERPRISE_NAV_CREDIT_CALCULATION}`,
    hideIfNonFinancialUser: true,
  },
  {
    section: NavSection.RD_TAX_CREDIT,
    path: Path.EXPORT_TAX,
    title: 'Tax Documents',
    icon: <ExportIcon />,
    id: `${AnalyticIdentifier.ENTERPRISE_NAV_EXPORT_QRE}`,
    hideIfNonFinancialUser: true,
  },
];

export const isRdPathAvailable = ({
  isNonFinancialUser,
  path,
}: {
  isNonFinancialUser: boolean;
  path: RdPath;
}): boolean => {
  const { hideIfNonFinancialUser } = RD_PATHS.find((p) => p.path === path) ?? {};

  if (hideIfNonFinancialUser && isNonFinancialUser) {
    return false;
  }

  return true;
};

export const getRdNavSection = ({
  companyId,
  fiscalYearId,
  isNonFinancialUser,
}: {
  isNonFinancialUser: boolean;
  companyId: string;
  fiscalYearId: string;
}): SideNavProps['sections'] => {
  const rdNavItems = RD_PATHS.filter((rdPathItem) =>
    isRdPathAvailable({ isNonFinancialUser, path: rdPathItem.path }),
  ).reduce((acc: SideNavItemProps[], rdPathItem) => {
    const { icon, id, path, title } = rdPathItem;
    acc.push({
      id,
      title,
      icon,
      to: getPath({ path, companyId, fiscalYearId }),
    });
    return acc;
  }, []);

  return [{ title: NavSection.RD_TAX_CREDIT, items: rdNavItems }];
};
